import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2, Plus, Trash2 } from "lucide-react";
import axios from "axios";
import Reveal, { SectionHeader } from "./Reveal";
import { RESTRICTION_OPTIONS } from "@/data/wedding";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  firstname: "",
  lastname: "",
  email: "",
  attending: "",
  accompanied: false,
  companions: [{ firstname: "", lastname: "", restrictions: [], allergies: "" }],
  restrictions: [],
  allergies: "",
  message: "",
};

const inputClass =
  "w-full rounded-xl border border-line bg-paper px-4 py-3.5 text-ink placeholder:text-fog/70 outline-none transition focus:border-terracotta focus:ring-2 focus:ring-terracotta/20";

const labelClass = "mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-taupe";

const ErrorText = ({ children }) =>
  children ? (
    <p className="mt-1.5 text-xs text-[#B4574A]">{children}</p>
  ) : null;

export default function Rsvp() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const toggleRestriction = (value) => {
    setForm((f) => {
      if (f.restrictions.includes(value)) {
        return { ...f, restrictions: f.restrictions.filter((r) => r !== value) };
      }
      const next =
        value === "aucune"
          ? ["aucune"]
          : [...f.restrictions.filter((r) => r !== "aucune"), value];
      return { ...f, restrictions: next };
    });
  };

  const toggleCompanionRestriction = (i, value) =>
    setForm((f) => ({
      ...f,
      companions: f.companions.map((c, ci) => {
        if (ci !== i) return c;
        if (c.restrictions.includes(value)) {
          return { ...c, restrictions: c.restrictions.filter((r) => r !== value) };
        }
        const next =
          value === "aucune"
            ? ["aucune"]
            : [...c.restrictions.filter((r) => r !== "aucune"), value];
        return { ...c, restrictions: next };
      }),
    }));

  const setCompanion = (i, key, value) =>
    setForm((f) => ({
      ...f,
      companions: f.companions.map((c, ci) => (ci === i ? { ...c, [key]: value } : c)),
    }));

  const addCompanion = () =>
    setForm((f) => ({
      ...f,
      companions: [
        ...f.companions,
        { firstname: "", lastname: "", restrictions: [], allergies: "" },
      ],
    }));

  const removeCompanion = (i) =>
    setForm((f) => ({
      ...f,
      companions: f.companions.filter((_, ci) => ci !== i),
    }));

  const validate = () => {
    const e = {};
    if (!form.firstname.trim()) e.firstname = "Votre prénom est requis";
    if (!form.lastname.trim()) e.lastname = "Votre nom est requis";
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      e.email = "Merci de saisir une adresse email valide";
    if (!form.attending) e.attending = "Merci de sélectionner une réponse";
    if (form.attending === "oui" && form.accompanied) {
      form.companions.forEach((c, i) => {
        if (!c.firstname.trim()) e[`companion-${i}`] = "Prénom de l'accompagnant requis";
        if (c.restrictions.length === 0)
          e[`companion-restrictions-${i}`] =
            "Sélectionnez les restrictions de cet accompagnant (ou « Pas de restriction particulière »)";
      });
    }
    if (form.attending === "oui" && form.restrictions.length === 0)
      e.restrictions = "Merci de sélectionner au moins une option";
    if (form.restrictions.includes("allergies") && !form.allergies.trim()) {
      e.allergies = "Merci de préciser vos allergies";
    }
    return e;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;
    setLoading(true);
    try {
      const isPresent = form.attending === "oui";
      const payload = {
        firstname: form.firstname.trim(),
        lastname: form.lastname.trim(),
        email: form.email.trim(),
        attending: form.attending,
        accompanied: isPresent && form.accompanied,
        companions:
          isPresent && form.accompanied
            ? form.companions.map((c) => ({
                firstname: c.firstname.trim(),
                lastname: c.lastname.trim(),
                restrictions: c.restrictions.map(
                  (v) => RESTRICTION_OPTIONS.find((o) => o.value === v)?.label || v
                ),
                allergies: c.restrictions.includes("allergies") ? c.allergies.trim() : "",
              }))
            : [],
        restrictions: form.restrictions.map(
          (v) => RESTRICTION_OPTIONS.find((o) => o.value === v)?.label || v
        ),
        allergies: form.restrictions.includes("allergies") ? form.allergies.trim() : "",
        message: form.message.trim(),
      };
      await axios.post(`${API}/rsvp`, payload);
      setDone(payload);
      window.scrollTo({ top: window.scrollY });
    } catch (err) {
      setErrors({
        global: "Une erreur est survenue lors de l'envoi, merci de réessayer.",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm(initialForm);
    setErrors({});
    setDone(null);
  };

  return (
    <section id="rsvp" className="scroll-mt-20 py-24 lg:py-32">
      <div className="mx-auto max-w-3xl px-5 sm:px-8">
        <SectionHeader
          eyebrow="02 — Répondre à l'invitation"
          title={
            <>
              Serez-vous des nôtres ?
            </>
          }
          subtitle={`Merci de confirmer votre présence avant le 28 février 2027 — chaque réponse nous compte.`}
        />

        <Reveal delay={0.1} className="mt-12">
          {done ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              data-testid="rsvp-success-card"
              className="rounded-3xl border border-line bg-paper p-8 text-center shadow-[0_10px_30px_rgba(44,38,35,0.05)] sm:p-12"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.15, type: "spring", stiffness: 200, damping: 14 }}
                className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-sage/15 text-sage-deep"
              >
                <Check className="h-8 w-8" strokeWidth={1.5} />
              </motion.div>
              <h3 className="mt-6 font-serif text-3xl text-ink sm:text-4xl">
                Merci {done.firstname} !
              </h3>
              <p className="mt-3 leading-relaxed text-taupe">
                Votre réponse a bien été enregistrée. Nous avons hâte de partager ce
                moment avec vous.
              </p>
              <div className="mx-auto mt-8 max-w-sm space-y-2 rounded-2xl bg-cream px-6 py-5 text-left text-sm">
                <p className="flex justify-between gap-4">
                  <span className="text-fog">Présence</span>
                  <span className="font-medium text-ink">
                    {done.attending === "oui" ? "Oui, je serai présent(e)" : "Non, je ne pourrai pas être là"}
                  </span>
                </p>
                {done.attending === "oui" && (
                  <>
                    <p className="flex justify-between gap-4">
                      <span className="text-fog">Accompagnants</span>
                      <span className="font-medium text-ink">
                        {done.accompanied ? done.companions.length : 0}
                      </span>
                    </p>
                  </>
                )}
              </div>
              <button
                type="button"
                data-testid="rsvp-modify-button"
                onClick={resetForm}
                className="mt-8 rounded-full border border-ink/15 px-7 py-3 text-sm font-medium text-ink transition-all duration-300 hover:-translate-y-0.5 hover:border-terracotta hover:text-terracotta"
              >
                Modifier ma réponse
              </button>
            </motion.div>
          ) : (
            <form
              onSubmit={onSubmit}
              noValidate
              data-testid="rsvp-form"
              className="rounded-3xl border border-line bg-paper p-6 shadow-[0_10px_30px_rgba(44,38,35,0.05)] sm:p-10"
            >
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="rsvp-firstname" className={labelClass}>
                    Prénom *
                  </label>
                  <input
                    id="rsvp-firstname"
                    data-testid="rsvp-input-firstname"
                    className={inputClass}
                    placeholder="Votre prénom"
                    value={form.firstname}
                    onChange={(e) => set("firstname", e.target.value)}
                  />
                  <ErrorText>{errors.firstname}</ErrorText>
                </div>
                <div>
                  <label htmlFor="rsvp-lastname" className={labelClass}>
                    Nom *
                  </label>
                  <input
                    id="rsvp-lastname"
                    data-testid="rsvp-input-lastname"
                    className={inputClass}
                    placeholder="Votre nom"
                    value={form.lastname}
                    onChange={(e) => set("lastname", e.target.value)}
                  />
                  <ErrorText>{errors.lastname}</ErrorText>
                </div>
              </div>

              <div className="mt-6">
                <label htmlFor="rsvp-email" className={labelClass}>
                  Adresse email <span className="normal-case tracking-normal text-fog">(optionnel)</span>
                </label>
                <input
                  id="rsvp-email"
                  type="email"
                  data-testid="rsvp-input-email"
                  className={inputClass}
                  placeholder="vous@exemple.fr"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                />
                <ErrorText>{errors.email}</ErrorText>
              </div>

              <div className="mt-8">
                <span className={labelClass}>Serez-vous présent(e) ? *</span>
                <div className="grid gap-3 sm:grid-cols-2">
                  {[
                    { v: "oui", label: "Oui, je serai présent(e)", testid: "rsvp-radio-present" },
                    { v: "non", label: "Non, je ne pourrai pas être là", testid: "rsvp-radio-absent" },
                  ].map((o) => (
                    <label
                      key={o.v}
                      className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-4 transition ${
                        form.attending === o.v
                          ? "border-sage bg-sage/5"
                          : "border-line bg-paper hover:border-sage/50"
                      }`}
                    >
                      <input
                        type="radio"
                        name="attending"
                        data-testid={o.testid}
                        className="sr-only"
                        checked={form.attending === o.v}
                        onChange={() => set("attending", o.v)}
                      />
                      <span
                        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                          form.attending === o.v ? "border-sage" : "border-line"
                        }`}
                      >
                        {form.attending === o.v && (
                          <span className="h-2.5 w-2.5 rounded-full bg-sage" />
                        )}
                      </span>
                      <span className="text-sm text-ink">{o.label}</span>
                    </label>
                  ))}
                </div>
                <ErrorText>{errors.attending}</ErrorText>
              </div>

              {form.attending === "oui" && (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="mt-8 space-y-6 overflow-hidden"
                >
                  <div className="space-y-4 rounded-2xl bg-cream/60 p-5 sm:p-7">
                  <div>
                    <span className={labelClass}>Vos préférences et restrictions alimentaires *</span>
                    <div className="grid gap-2.5 sm:grid-cols-2">
                      {RESTRICTION_OPTIONS.map((o) => {
                        const checked = form.restrictions.includes(o.value);
                        return (
                          <label
                            key={o.value}
                            className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 transition ${
                              checked
                                ? "border-sage bg-sage/5"
                                : "border-line bg-paper hover:border-sage/50"
                            }`}
                          >
                            <input
                              type="checkbox"
                              data-testid={`rsvp-checkbox-restriction-${o.value}`}
                              className="sr-only"
                              checked={checked}
                              onChange={() => toggleRestriction(o.value)}
                            />
                            <span
                              className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition ${
                                checked ? "border-sage bg-sage text-sand" : "border-line bg-paper"
                              }`}
                            >
                              {checked && <Check className="h-3.5 w-3.5" />}
                            </span>
                            <span className="text-sm text-ink">{o.label}</span>
                          </label>
                        );
                      })}
                    </div>
                    <ErrorText>{errors.restrictions}</ErrorText>
                    {form.restrictions.includes("allergies") && (
                      <div className="mt-4">
                        <textarea
                          data-testid="rsvp-input-allergies"
                          rows={3}
                          className={inputClass}
                          placeholder="Précisez vos allergies (fruits à coque, gluten, lactose…)"
                          value={form.allergies}
                          onChange={(e) => set("allergies", e.target.value)}
                        />
                        <ErrorText>{errors.allergies}</ErrorText>
                      </div>
                    )}
                  </div>
                  </div>

                  <div className="space-y-4 rounded-2xl bg-cream/60 p-5 sm:p-7">
                  <label className="flex cursor-pointer items-center gap-3">
                    <input
                      type="checkbox"
                      data-testid="rsvp-checkbox-accompanied"
                      className="sr-only"
                      checked={form.accompanied}
                      onChange={(e) => set("accompanied", e.target.checked)}
                    />
                    <span
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition ${
                        form.accompanied ? "border-sage bg-sage text-sand" : "border-line bg-paper"
                      }`}
                    >
                      {form.accompanied && <Check className="h-3.5 w-3.5" />}
                    </span>
                    <span className="text-sm text-ink">Je viens accompagné(e)</span>
                  </label>

                  {form.accompanied && (
                    <div className="space-y-4">
                      {form.companions.map((c, i) => (
                        <div key={i} className="space-y-3 rounded-xl border border-line bg-paper/70 p-4">
                          <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-start">
                            <div>
                              <input
                                data-testid={`rsvp-input-companion-name-${i}`}
                                className={inputClass}
                                placeholder="Prénom de l'accompagnant"
                                value={c.firstname}
                                onChange={(e) => setCompanion(i, "firstname", e.target.value)}
                              />
                              <ErrorText>{errors[`companion-${i}`]}</ErrorText>
                            </div>
                            <input
                              data-testid={`rsvp-input-companion-lastname-${i}`}
                              className={inputClass}
                              placeholder="Nom (optionnel)"
                              value={c.lastname}
                              onChange={(e) => setCompanion(i, "lastname", e.target.value)}
                            />
                            {form.companions.length > 1 && (
                              <button
                                type="button"
                                data-testid={`rsvp-remove-companion-${i}`}
                                onClick={() => removeCompanion(i)}
                                aria-label="Retirer cet accompagnant"
                                className="flex h-[52px] w-[52px] items-center justify-center justify-self-start rounded-xl border border-line text-fog transition hover:border-terracotta hover:text-terracotta sm:justify-self-auto"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                          <div>
                            <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.15em] text-fog">
                              Restrictions alimentaires de cet accompagnant *
                            </span>
                            <div className="grid gap-2 sm:grid-cols-2">
                              {RESTRICTION_OPTIONS.map((o) => {
                                const checked = c.restrictions.includes(o.value);
                                return (
                                  <label
                                    key={o.value}
                                    className={`flex cursor-pointer items-center gap-2.5 rounded-lg border px-3 py-2.5 transition ${
                                      checked
                                        ? "border-sage bg-sage/5"
                                        : "border-line bg-paper hover:border-sage/50"
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      data-testid={`rsvp-checkbox-companion-restriction-${i}-${o.value}`}
                                      className="sr-only"
                                      checked={checked}
                                      onChange={() => toggleCompanionRestriction(i, o.value)}
                                    />
                                    <span
                                      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border transition ${
                                        checked ? "border-sage bg-sage text-sand" : "border-line bg-paper"
                                      }`}
                                    >
                                      {checked && <Check className="h-3 w-3" />}
                                    </span>
                                    <span className="text-xs text-ink sm:text-sm">{o.label}</span>
                                  </label>
                                );
                              })}
                            </div>
                            <ErrorText>{errors[`companion-restrictions-${i}`]}</ErrorText>
                            {c.restrictions.includes("allergies") && (
                              <input
                                data-testid={`rsvp-input-companion-allergies-${i}`}
                                className={`${inputClass} mt-2.5`}
                                placeholder="Précisez les allergies de cet accompagnant"
                                value={c.allergies}
                                onChange={(e) => setCompanion(i, "allergies", e.target.value)}
                              />
                            )}
                          </div>
                        </div>
                      ))}
                      <button
                        type="button"
                        data-testid="rsvp-add-companion"
                        onClick={addCompanion}
                        className="inline-flex items-center gap-2 rounded-full border border-dashed border-sage px-5 py-2.5 text-sm text-sage-deep transition hover:bg-sage/10"
                      >
                        <Plus className="h-4 w-4" />
                        Ajouter un accompagnant (conjoint, enfants…)
                      </button>
                    </div>
                  )}
                  </div>
                </motion.div>
              )}

              <div className="mt-8 grid gap-6">
                <div>
                  <label htmlFor="rsvp-message" className={labelClass}>
                    Un mot pour les mariés
                  </label>
                  <textarea
                    id="rsvp-message"
                    data-testid="rsvp-input-message"
                    rows={3}
                    className={inputClass}
                    placeholder="Votre message (optionnel)"
                    value={form.message}
                    onChange={(e) => set("message", e.target.value)}
                  />
                </div>
              </div>

              {errors.global && (
                <div
                  data-testid="rsvp-error-banner"
                  className="mt-6 rounded-xl border border-[#B4574A]/30 bg-[#B4574A]/5 px-4 py-3 text-sm text-[#B4574A]"
                >
                  {errors.global}
                </div>
              )}

              <button
                type="submit"
                data-testid="rsvp-submit-button"
                disabled={loading}
                className="mt-8 flex w-full items-center justify-center gap-3 rounded-full bg-ink px-8 py-4 text-sm font-medium tracking-wide text-sand transition-all duration-300 hover:-translate-y-0.5 hover:bg-terracotta hover:shadow-[0_14px_30px_rgba(200,125,101,0.35)] disabled:translate-y-0 disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Envoi en cours…
                  </>
                ) : (
                  "Envoyer ma réponse"
                )}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
